import { Brackets, QueryRunner } from "typeorm";
import { ConnectionPool, getQueryBuilder } from "../Connection";
import Model from "../Model";
import JoinOperatorQueryBuilder from "./JoinOperatorQueryBuilder";
import WhereQueryBuilder, { SubQueryType } from "./WhereQueryBuilder";

class QueryBuilder extends WhereQueryBuilder {

    protected _table: any;
    protected _alias: any;
    protected _select: any[][] = [];
    protected _having: any[][] = [];
    protected _join: any[][] = [];
    protected _orderBy: string[][] = [];
    protected _groupBy: string[] = [];
    protected _limit?: number;
    protected _offset?: number;
    protected _take?: number;
    protected _skip?: number;
    protected _withSoftDeleted?: boolean = false;
    protected _cache: any[] = [];
    protected _modelClass: any;
    protected _entity: any;
    protected _dataSourceName?: string;
    protected _queryRunner?: QueryRunner | null;
    protected _use: 'table' | 'entity' | string;

    constructor(modelClass?: Model | null, use: 'table' | 'entity' | string = 'entity') {

        super()

        this._use = use

        if(modelClass) {
            
            this._modelClass = modelClass
    
            this._entity = this._modelClass.entity
    
            this._table = this._modelClass.table
        }

    }

    protected modelizeData(data: any): any {
        
        if(!Array.isArray(data)) {
            return this._modelClass ? new this._modelClass({ 
                exists: true, 
                attributes: {...data} 
            }) : data
        } else {
            return data.map((each: any) => this.modelizeData(each))
        }
    }

    public now() {

        return new Date().toISOString()
    }

    public from(model: Model, alias?: string) {
        this._entity = (model as any).entity
        this._table = (model as any).table
        this._alias = alias
        return this
    }

    public as(alias: string) {
        this._alias = alias
        return this
    }

    public select(selectOrSub: string | SubQueryType, ...select: string[]): this {
        if(typeof selectOrSub === 'function') {

            const subQuery = new QueryBuilder(null, this._use)

            selectOrSub(subQuery)

            const query = subQuery.querying('select')

            this._parameters = {
                ...this._parameters,
                ...query.getParameters()
            }

            this._select = [...this._select, [`(${query.getQuery()})`, select[0]]]

        } else {
            this._select = [...this._select, selectOrSub.trim().split(" as "), ...select.map((item) => item.trim().split(" as "))]
        }

        return this
    }

    protected joinFactory(tableRaw: string, column: string | ((joinQuery: JoinOperatorQueryBuilder) => void), colomnToJoin: string, type: "INNER_JOIN" | "LEFT_JOIN" | "RIGHT_JOIN"): this {

        // const id = `${this._uniqueParameterID}${Object.keys(this._parameters).length + 1}`

        const [table, alias] = tableRaw.trim().split(" as ")

        if(typeof column === 'function') {

            const joinQuery = new JoinOperatorQueryBuilder()

            column(joinQuery)

            this._parameters = {
                ...this._parameters,
                ...joinQuery.getParameters()
            }

            const operators = joinQuery.getJoinOperator().reduce<string>((result, [operator, statement], index) => {
                const isFirst = index === 0

                return result + `${isFirst ? '' : ` ${statement} `}${operator}`
            }, '')

            this._join.push([table, alias || table, operators, type])

        } else {
            
            this._join.push([table, alias || table, `${column} = ${colomnToJoin}`, type])
        }
  
        return this
    }

    public innerJoin(tableRaw: string, column: string | ((subQuery: JoinOperatorQueryBuilder) => void), colomnToJoin?: string): this {

        return this.joinFactory(tableRaw, column, colomnToJoin!, 'INNER_JOIN')
    }

    public leftJoin(tableRaw: string, column: string | ((subQuery: JoinOperatorQueryBuilder) => void), colomnToJoin?: string): this {

        return this.joinFactory(tableRaw, column, colomnToJoin!, 'LEFT_JOIN')
    }

    public rightJoin(tableRaw: string, column: string | ((subQuery: JoinOperatorQueryBuilder) => void), colomnToJoin?: string): this {

        return this.joinFactory(tableRaw, column, colomnToJoin!, 'RIGHT_JOIN')
    }

    protected havingFactory(column: string, operator: string, needle: SubQueryType | any, statement: 'AND' | 'OR'): this {

        if(typeof needle === 'function') {

            const subQuery = new QueryBuilder(null, this._use)

            needle(subQuery)

            const query = subQuery.querying('select')

            this._parameters = {
                ...this._parameters,
                ...query.getParameters()
            }

            this._having.push([`${column} ${operator} (${query.getQuery()})`, statement])

        } else {

            const id = `${this._uniqueParameterID}${Object.keys(this._parameters).length + 1}`

            this._having.push([`${column} ${operator} :${id}`, statement])

            this._parameters[id] = needle
        }
        
        return this
    }

    public having(column: string, operator: string | SubQueryType | any, needle?: SubQueryType | any): this {

        return needle ? (
            this.havingFactory(column, operator, needle, 'AND')
        ) : (
            this.havingFactory(column, '=', operator, 'AND')
        )
    }

    public orHaving(column: string, operator: string | SubQueryType | any, needle?: SubQueryType | any): this {

        return needle ? (
            this.havingFactory(column, operator, needle, 'OR')
        ) : (
            this.havingFactory(column, '=', operator, 'OR')
        )
    }

    public orderBy(orderBy: string, order: 'ASC' | 'DESC'): this {
        this._orderBy.push([orderBy, order])
        return this
    }

    public groupBy(groupBy: string): this {
        this._groupBy.push(groupBy)
        return this
    }

    public limit(value: number): this {

        this._limit = value

        return this
    }

    public take(value: number): this {

        this._take = value

        return this
    }

    public offset(value: number): this {

        this._offset = value

        return this
    }

    public skip(value: number): this {

        this._skip = value

        return this
    }

    public withSoftDeleted(): this {

        this._withSoftDeleted = true

        return this
    }

    public async first(): Promise<Model> {
        try {
            
            const query = await this.querying('select').getRawOne()
            
            return query ? this.modelizeData(query) : null

        } catch (error) {
            throw new Error(error.message)
        }
    }

    public async get(): Promise<Model[]> {
        try {
            
            const query = await this.querying('select').getRawMany()

            return this.modelizeData(query)
            
        } catch (error) {
            throw new Error(error.message)
        }
    }

    public async count(select: string = '*'): Promise<number> {
        try {
            
            const query = await this.querying('select').getCount()
            
            return query

        } catch (error) {
            throw new Error(error.message)
        }
    }

    public async paginate(page: number, size: number = 10): Promise<{ count: number, models: Model[]}> {
        
        const models = await this.skip(page * size - size).take(size).get()

        this._take = this._skip = undefined
        
        const count = await this.count()

        return { count, models }
    }

    public async insert(data: Record<string, any>): Promise<{id: number}[]> {
        try {
            const query = await this.querying('insert', data).execute()
            
            return query.identifiers

        } catch (error) {
            throw new Error(error.message)
        }
    }

    public async update(data: Record<string, any>): Promise<number> {
        try {
            const query = await this.querying('update', data).execute()
            
            return query.affected

        } catch (error) {
            throw new Error(error.message)
        }
    }

    public async restore(): Promise<any> {
        try {
            const query = await this.querying('restore').execute()
            
            return query.affected
        } catch (error) {
            throw new Error(error.message)
        }
    }

    public async softDelete(): Promise<any> {
        try {
            const query = await this.querying('soft-delete').execute()
            
            return query.affected
        } catch (error) {
            throw new Error(error.message)
        }
    }

    public async delete(): Promise<number> {
        try {
            const query = await this.querying('delete').execute()

            return query.affected
        } catch (error) {
            throw new Error(error.message)
        }
    }

    public cache(...cacheArgs: any[]): this {
        this._cache = cacheArgs
        return this
    }

    public pool(pool: ConnectionPool): this {

        this._queryRunner = pool.getQueryRunner();

        this._dataSourceName = pool.getDataSourceName();
        
        return this
    }

    public connect(dataSourceName: string = 'default'): this {
        this._dataSourceName = dataSourceName;
        return this
    }

    public getQuery(): string {
        try {
            
            const query = this.querying('select').getQuery()
            
            return query

        } catch (error) {
            throw new Error(error.message)
        }
    }

    public querying(type: 'insert' | 'update' | 'delete' | 'soft-delete' | 'select' | 'restore', data?: any):any {

        let connection: any = getQueryBuilder(this._dataSourceName, this._queryRunner)

        const entity = this._entity

        const table = this._table

        const useForExecution = this._use === 'table' ? table : entity

        switch(type) {
            case "insert": 
                connection = connection.insert().into(useForExecution).values(data)
                break;
            case "update": 
                connection = connection.update(useForExecution).set(data)
                break;
            case "delete": 
                connection = connection.delete().from(useForExecution)
                break;
            case "soft-delete": 
                connection = connection.softDelete().from(useForExecution)
                break;
            case "restore": 
                connection = connection.restore().from(useForExecution)
                break;
            case "select": 
                if(this._select.length) {
                    connection = this._select.reduce((result, select, index) => {
                        const isFirst = index === 0
                        
                        if(isFirst) {
                            return result.select(...select)
                        } else {
                            return result.addSelect(...select)
                        }
                    }, connection)
                }

                connection = connection.from(useForExecution, this._alias || table)

                if(this._having.length) {
                    connection = this._having.reduce((result, [having, condition], index) => {
                        const isFirst = index === 0
                        
                        if (isFirst) {
                            return result.having(having)
        
                        } else if (condition === 'AND') {
                            return result.andHaving(having)
        
                        } else {
                            return result.orHaving(having)
        
                        }
        
                    }, connection)
                }  
                
                if(this._join.length) {

                    connection = this._join.reduce((result, [joinTable, joinTableAlias, operator, type]) => {
                        
                        if (type === 'LEFT_JOIN') {
                            
                            return result.leftJoin(joinTable, joinTableAlias, operator)
        
                        } else if (type === 'RIGHT_JOIN') {
                            
                            return result.rightJoin(joinTable, joinTableAlias, operator)

                        } else {

                            return result.innerJoin(joinTable, joinTableAlias, operator)
                        }
        
                    }, connection)
                } 
    
                if(this._limit) {
                    connection = connection.limit(this._limit)
                }
    
                if(this._offset) {
                    connection = connection.offset(this._limit)
                }
    
                if(this._take) {
                    connection = connection.take(this._take)
                }
    
                if(this._skip) {
                    connection = connection.skip(this._skip)
                }
    
                if(this._orderBy.length) {
                    connection = this._orderBy.reduce((result, args, index) => {
    
                        const isFirst = index === 0
                        
                        if(isFirst) {
    
                            return result.orderBy(...args)
                        } else {
                        
                            return result.addOrderBy(...args)
                        }
                    }, connection)
                }
    
                if(this._groupBy.length) {
                    connection = this._groupBy.reduce((result, groupBy, index) => {
    
                        const isFirst = index === 0
                        
                        if(isFirst) {
    
                            return result.groupBy(groupBy)
                        } else {
                        
                            return result.addGroupBy(groupBy)
                        }
                    }, connection)
                }
    
                if(this._cache.length) {
                    connection = connection.cache(...this._cache)
                }

                if(this._withSoftDeleted) {
                    connection = connection.withDeleted()
                }

                break;
            default:
                break;
        }

        if(this._where.length) {
            connection = this.whereQuery(connection, this._where)
        }

        if(Object.keys(this._parameters).length) {
            connection.setParameters(this._parameters)
        }
        
        return connection
    }

    protected whereQuery(connection: any, where: any[]): any {

        return where.reduce((result, [where, condition, other], index) => {
            const isFirst = index === 0
            
            if (isFirst) {
                return result.where(other === "BRACKET" ? new Brackets((qb) => this.whereQuery(qb, where)) : where)

            } else if (condition === 'AND') {
                return result.andWhere(other === "BRACKET" ? new Brackets((qb) => this.whereQuery(qb, where)) : where)

            } else {
                return result.orWhere(other === "BRACKET" ? new Brackets((qb) => this.whereQuery(qb, where)) : where)

            }

        }, connection)
    }
}

export default QueryBuilder